import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sprout, Wheat, CloudRain, TrendingUp, Leaf, HandshakeIcon, Fish, MapPin, CalendarIcon, DollarSignIcon, ChevronDown, ChevronUp } from 'lucide-react';
const projects = [{
  title: 'Agriculture Cluster Development Project (ACDP)',
  description: 'Raising agricultural productivity and commercialization of selected commodities in specified geographic clusters across Uganda.',
  status: 'Active',
  funder: 'World Bank / Government of Uganda',
  period: '2015 – 2025',
  icon: Wheat,
  color: 'bg-amber-600',
  highlights: ['Supports 12 priority commodities', 'Targets 58 districts in 5 agro-ecological zones', 'Strengthens extension services and market linkages']
}, {
  title: 'Regional Pastoral Livelihoods Resilience Project (RPLRP)',
  description: 'Enhancing livelihood resilience of pastoral and agro-pastoral communities in cross-border drought-prone areas of Uganda, Kenya, and Ethiopia.',
  status: 'Active',
  funder: 'World Bank / IGAD',
  period: '2014 – 2024',
  icon: MapPin,
  color: 'bg-orange-600',
  highlights: ['Covers Karamoja sub-region', 'Improves natural resource management', 'Builds climate resilience for pastoralists']
}, {
  title: 'Agriculture Value Chain Development Program (AVCDP)',
  description: 'Strengthening priority agricultural value chains to improve productivity, value addition, and market access for smallholder farmers.',
  status: 'Active',
  funder: 'African Development Bank',
  period: '2018 – 2025',
  icon: TrendingUp,
  color: 'bg-emerald-600',
  highlights: ['Focus on rice, cassava, maize, and beans', 'Supports agro-processing and value addition', 'Targets youth and women in agriculture']
}, {
  title: 'Uganda Climate Smart Agriculture Program',
  description: 'Promoting climate-smart agricultural practices to increase productivity, enhance resilience, and reduce greenhouse gas emissions in the agricultural sector.',
  status: 'Active',
  funder: 'Green Climate Fund / FAO',
  period: '2020 – 2027',
  icon: CloudRain,
  color: 'bg-sky-600',
  highlights: ['Climate-resilient crop varieties', 'Sustainable land and water management', 'Weather information services for farmers']
}, {
  title: 'Project for Restoration of Livelihoods in Northern Region (PRELNOR)',
  description: 'Supporting smallholder farmers in Northern Uganda to improve food security, increase incomes, and build resilient livelihoods after decades of conflict.',
  status: 'Active',
  funder: 'IFAD / Government of Uganda',
  period: '2015 – 2024',
  icon: HandshakeIcon,
  color: 'bg-violet-600',
  highlights: ['Covers 9 districts in Northern Uganda', 'Rural infrastructure development', 'Community-driven development approach']
}, {
  title: 'Multi-Sectoral Food Security & Nutrition Project (MSFSNP)',
  description: 'Addressing food insecurity and malnutrition through an integrated multi-sectoral approach combining agriculture, health, and social protection.',
  status: 'Active',
  funder: 'World Bank / Global Agriculture & Food Security Program',
  period: '2017 – 2025',
  icon: Sprout,
  color: 'bg-green-600',
  highlights: ['Nutrition-sensitive agriculture interventions', 'Community nutrition education', 'Targets vulnerable households in 15 districts']
}, {
  title: 'Aquaculture Development Project',
  description: 'Developing sustainable aquaculture and fisheries management to boost fish production, create employment, and improve nutrition outcomes.',
  status: 'Active',
  funder: 'IsDB / Government of Uganda',
  period: '2019 – 2026',
  icon: Fish,
  color: 'bg-blue-600',
  highlights: ['Cage fish farming on major lakes', 'Hatchery and feed production support', 'Fish quality and safety standards']
}, {
  title: 'Sustainable Land Management Project',
  description: 'Combating land degradation and promoting sustainable land management practices to protect agricultural productivity and natural resources.',
  status: 'Active',
  funder: 'GEF / World Bank',
  period: '2018 – 2025',
  icon: Leaf,
  color: 'bg-teal-600',
  highlights: ['Soil and water conservation', 'Agroforestry and reforestation', 'Community watershed management']
}];
export function ProjectsSection() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const visibleProjects = showAll ? projects : projects.slice(0, 6);
  return <section className="py-16 bg-white" data-id="element-374">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-375">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 gap-4" data-id="element-376">
          <div data-id="element-377">
            <div className="inline-flex items-center gap-2 bg-uganda-light-green text-uganda-green text-sm font-semibold px-3 py-1 rounded-full mb-4" data-id="element-378">
              <Sprout className="w-4 h-4" data-id="element-379" />
              Government Initiatives
            </div>
            <h2 className="text-3xl font-bold text-uganda-black mb-3" data-id="element-380">
              Ministry Projects
            </h2>
            <p className="text-gray-600 max-w-2xl" data-id="element-381">
              Explore ongoing projects by the Ministry of Agriculture, Animal
              Industry and Fisheries transforming Uganda's agricultural sector
              through strategic investments and partnerships.
            </p>
          </div>
          <Link to="/projects" className="hidden md:flex items-center text-uganda-green font-semibold hover:text-green-800 transition-colors whitespace-nowrap" data-id="element-382">
            View All Projects <ArrowRight className="w-5 h-5 ml-2" data-id="element-383" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-384">
          {visibleProjects.map((project, index) => {
          const Icon = project.icon;
          const isExpanded = expandedIndex === index;
          return <motion.div key={project.title} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.45,
            delay: index * 0.07
          }} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-all shadow-sm hover:shadow-md" data-id="element-385">
                <div className="p-6" data-id="element-386">
                  <div className="flex items-start gap-4 mb-4" data-id="element-387">
                    <div className={`w-11 h-11 rounded-lg ${project.color} flex items-center justify-center flex-shrink-0`} data-id="element-388">
                      <Icon className="w-5 h-5 text-white" data-id="element-389" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-390">
                      <h3 className="text-lg font-bold text-uganda-black leading-snug mb-1" data-id="element-391">
                        {project.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500" data-id="element-392">
                        <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium" data-id="element-393">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full" data-id="element-394" />
                          {project.status}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-395">
                          <CalendarIcon className="w-3 h-3" data-id="element-396" />
                          {project.period}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm leading-relaxed mb-3" data-id="element-397">
                    {project.description}
                  </p>

                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-3" data-id="element-398">
                    <DollarSignIcon className="w-3.5 h-3.5" data-id="element-399" />
                    <span className="font-medium" data-id="element-400">Funded by:</span>{' '}
                    {project.funder}
                  </div>

                  <button onClick={() => setExpandedIndex(isExpanded ? null : index)} className="inline-flex items-center gap-1 text-sm font-semibold text-uganda-green hover:text-green-800 transition-colors" data-id="element-401">
                    {isExpanded ? 'Hide' : 'Key'} Highlights
                    {isExpanded ? <ChevronUp className="w-4 h-4" data-id="element-402" /> : <ChevronDown className="w-4 h-4" data-id="element-403" />}
                  </button>

                  <AnimatePresence data-id="element-404">
                    {isExpanded && <motion.ul initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden mt-3 space-y-2" data-id="element-405">
                        {project.highlights.map((highlight, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-406">
                            <span className="w-1.5 h-1.5 bg-uganda-green rounded-full mt-1.5 flex-shrink-0" data-id="element-407" />
                            {highlight}
                          </li>)}
                      </motion.ul>}
                  </AnimatePresence>
                </div>
              </motion.div>;
        })}
        </div>

        {projects.length > 6 && <div className="mt-8 text-center" data-id="element-408">
            <button onClick={() => setShowAll(!showAll)} className="inline-flex items-center gap-2 bg-uganda-light-green text-uganda-green px-6 py-3 rounded-lg font-semibold hover:bg-green-100 transition-colors" data-id="element-409">
              {showAll ? 'Show Less' : `View All ${projects.length} Projects`}
              {showAll ? <ChevronUp className="w-5 h-5" data-id="element-410" /> : <ChevronDown className="w-5 h-5" data-id="element-411" />}
            </button>
          </div>}

        <div className="mt-6 text-center md:hidden" data-id="element-412">
          <Link to="/projects" className="inline-flex items-center justify-center gap-2 bg-white border border-gray-200 text-uganda-black px-6 py-3 rounded-md font-semibold w-full" data-id="element-413">
            View All Projects <ArrowRight className="w-4 h-4" data-id="element-414" />
          </Link>
        </div>
      </div>
    </section>;
}